import { useAuth } from "@via/hooks/useAuth.tsx";
import { useCallback, useEffect, useState, type JSX } from "react";
import { useSearchParams, Navigate, useLocation } from "react-router-dom";
import {
  useGetUserInvitationCheck,
  useAcceptUserInvitation,
} from "@via/pages/account/admin/admin.api.tsx";
import Login from "@via/pages/authentication/login";
import { LogoSpinner } from "@via/components/loading/loading";
import { GenericError } from "@via/components/error";
import { ERROR, ROUTES } from "@via/utils/constants";
import { shouldBlockAccess } from "@via/utils/helpers";

export default function GuestSignIn(): JSX.Element {
  const { user } = useAuth();
  const { pathname } = useLocation();

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [hasInvited, setHasInvited] = useState(false);

  const {
    data: invitationDetails,
    isLoading,
    isSuccess,
    isError,
  } = useGetUserInvitationCheck({ token: token || "" });

  const { mutate: acceptUserInvitation, isPending: isAcceptingInvitation } =
    useAcceptUserInvitation();

  const onSuccess = useCallback(() => {
    if (hasInvited) {
      return;
    }
    setHasInvited(true);
    acceptUserInvitation({
      token: token || "",
    });
  }, [hasInvited, acceptUserInvitation, token]);

  // Trigger invitation acceptance when user is already logged in
  useEffect(() => {
    if (user?.session && pathname === ROUTES.GUEST_SIGNIN && !hasInvited) {
      onSuccess();
    }
  }, [user?.session, pathname, onSuccess, hasInvited]);

  // If you're already logged in, show loading while processing invitation
  if (user?.session && pathname === ROUTES.GUEST_SIGNIN) {
    // Additional logic for self-registered/invited users not fully onboarded
    const blockAccess = shouldBlockAccess(user);
    if (blockAccess) {
      return <Navigate to={ROUTES.SELF_SIGNUP} replace />;
    } else {
      return <Navigate to={ROUTES.ACTIVITY} replace />;
    }
  }

  return (
    <>
      {(isLoading || isAcceptingInvitation) && (
        <div className="via-loading-spinner">
          <LogoSpinner />
        </div>
      )}
      {isError && (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      )}
      {isSuccess && !isAcceptingInvitation && (
        <Login
          selfSignIn
          email={invitationDetails?.invitedEmail || ""}
          organizationName={""}
          token={token || ""}
          onSuccess={onSuccess}
        />
      )}
    </>
  );
}
